import type { TOC } from '@ember/component/template-only';
import { fn } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import {
  PrismIcon,
  PrismLoadingBar,
  PrismMessage,
  PrismPagination,
  PrismTable,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { gt, or } from 'ember-truth-helpers';
import GeneralNavigationIntercomHelpLink from 'lumen/components/general/navigation/intercom-help-link';
import type DestinationReportingScheduledReportsController from 'lumen/controllers/destination/reporting/scheduled-reports';

interface DestinationReportingScheduledReportsSignature {
  Args: {
    controller: DestinationReportingScheduledReportsController;
    model: unknown;
  };
}

<template>
  <div
    class="custom-reporting__page flex w-full flex-col gap-400 pt-500 pr-800 pb-500 pl-800"
  >
    {{#if @controller.model.reports.isRunning}}
      <div class="loading-bar-container">
        <PrismLoadingBar />
      </div>
    {{/if}}
    <div>
      {{#if @controller.access.hasCustomReportingEdit}}
        <LinkTo
          @route="market.custom-reporting.index"
          class="prism-link prism-link--secondary prism-link--standalone prism-link--small"
          data-test-id="custom-reporting-back-to-home"
        >
          <PrismIcon @color="inherit" @name="arrow-left" @size="small" />
          {{t "custom_reporting.home"}}
        </LinkTo>
      {{/if}}
      <div class="mt-200 flex justify-between">
        <h2 data-test-id="custom-reporting-scheduled-reports-header">
          {{t "custom_reporting.scheduled_custom_reports"}}
        </h2>
        {{#if
          (or @controller.model.reports.isRunning @controller.reports.length)
        }}
          <GeneralNavigationIntercomHelpLink
            @articleLink="/en/articles/9230760-scheduling-and-sending-dashboards-in-custom-reporting"
            @iconName="link-out"
            @isStandaloneLink={{true}}
            @linkText={{t "general.learn_more"}}
            @type="text-and-icon"
          />
        {{/if}}
      </div>
    </div>
    {{#if (or @controller.model.reports.isRunning @controller.reports.length)}}
      <div>
        <PrismTable
          @columns={{@controller.columns}}
          @isLoading={{@controller.model.reports.isRunning}}
          @nrOfEmptyRows={{3}}
          @nrOfLoadingRows={{6}}
          {{! @glint-expect-error: Incorrect type }}
          @onUpdateSorts={{@controller.onUpdateSorts}}
          @rows={{@controller.rows}}
          @sorts={{@controller.sorts}}
          @widthConstraint="eq-container"
          class="custom-reporting__table"
          data-test-id="scheduled-reports-table"
        />
      </div>
      {{#if (gt @controller.totalPages 1)}}
        <PrismPagination
          @lastPage={{@controller.totalPages}}
          @page={{@controller.page}}
          @setPage={{fn (mut @controller.page)}}
          data-test-id="scheduled-reports-table-pagination"
        />
      {{/if}}
    {{else}}
      <div class="flex justify-center" data-test-id="empty-scheduled-reports">
        <PrismMessage
          @description=""
          @illustration="100x100_list"
          @title={{t "custom_reporting.schedule_report.empty_state.title"}}
        >
          <:link>
            <GeneralNavigationIntercomHelpLink
              @articleLink="/en/articles/9230760-scheduling-and-sending-dashboards-in-custom-reporting"
              @iconName="link-out"
              @isStandaloneLink={{true}}
              @linkText={{t "custom_reporting.schedule_report.empty_state.cta"}}
              @type="text-and-icon"
            />
          </:link>
        </PrismMessage>
      </div>
    {{/if}}
  </div>
</template> satisfies TOC<DestinationReportingScheduledReportsSignature>;
